import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getReviewableProducts, submitReview } from "@/lib/reviews.functions";
import { supabase } from "@/integrations/supabase/client";
import { Star, ShieldCheck, X, Coins } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/review")({
  head: () => ({
    meta: [
      { title: "Write a review — TruWe" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReviewPage,
});

function ReviewPage() {
  const fn = useServerFn(getReviewableProducts);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["reviewable"],
    queryFn: () => fn(),
  });
  const items = data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#F7F7F5] px-5 py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#2C2C2C] md:text-3xl">Your reviews</h1>
            <p className="text-sm text-[#2C2C2C]/60">Share your experience with the protocols you've ordered.</p>
          </div>
          <Link to="/account" className="text-sm text-[#1E5EFF] hover:underline">← Account</Link>
        </header>

        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#1E5EFF]/20 bg-[#1E5EFF]/5 p-4">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1E5EFF]/10 text-[#1E5EFF]">
            <Coins size={16} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#2C2C2C]">Every review earns 50 TruCoins — including 1-star ones</p>
            <p className="mt-0.5 text-xs text-[#2C2C2C]/60">
              We reward you for taking the time to write, not for a good rating. 50 TruCoins (₹25) are
              credited whatever you score us. We only remove spam or abuse — never honest criticism.
            </p>

          </div>
        </div>


        {isLoading && <p className="text-sm text-[#2C2C2C]/60">Loading…</p>}
        {!isLoading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#D9D9D6] bg-white p-10 text-center">
            <p className="text-[#2C2C2C]/70">No reviewable products yet.</p>
            <p className="mt-1 text-sm text-[#2C2C2C]/50">Reviews unlock once your order is paid.</p>
          </div>
        )}

        <div className="space-y-4">
          {items.map((it: any) => (
            <ReviewCard key={`${it.orderId}:${it.productId}`} item={it} onSubmitted={refetch} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ item, onSubmitted }: { item: any; onSubmitted: () => void }) {
  const submitFn = useServerFn(submitReview);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [title, setTitle] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (item.alreadyReviewed) {
    // Customers see one neutral state for every submitted review — no hint of
    // whether it will be published.
    return (
      <div className="rounded-2xl border border-[#D9D9D6] bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-medium text-[#2C2C2C]">{item.title}</p>
            <p className="text-xs text-[#2C2C2C]/50">
              Order {item.orderName}
              {item.reviewSubmittedAt
                ? ` · Submitted ${new Date(item.reviewSubmittedAt).toLocaleDateString("en-IN")}`
                : ""}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            <ShieldCheck size={12} /> Submitted
          </span>
        </div>
        <p className="mt-3 text-xs text-[#2C2C2C]/60">
          Thank you for taking the time to write this. Your 50 TruCoins will be credited shortly —
          they are the same for every rating.
        </p>

      </div>
    );
  }

  async function uploadFile(file: File) {
    if (images.length >= 2) {
      toast.error("Max 2 photos");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Max 3 MB per photo");
      return;
    }
    setUploading(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) throw new Error("not signed in");
      const path = `${uid}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from("review-photos").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (upErr) throw upErr;
      const { data: signed } = await supabase.storage.from("review-photos").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (signed?.signedUrl) setImages((arr) => [...arr, signed.signedUrl]);
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function submit() {
    if (rating < 1) return toast.error("Please pick a star rating");
    if (comment.trim().length < 3) return toast.error("Please share a short comment");
    setBusy(true);
    try {
      const res = await submitFn({
        data: {
          productId: item.productId,
          orderId: item.orderId,
          rating,
          title: title.trim() || undefined,
          comment: comment.trim(),
          images,
        },
      });
      if (!res.ok) {
        toast.error(res.error || "Could not submit");
      } else {
        toast.success("Thank you for taking the time to write this. Your 50 TruCoins will be credited shortly.");
        onSubmitted();
      }
    } catch (e: any) {
      toast.error(e.message || "Could not submit");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-[#D9D9D6] bg-white p-5 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium text-[#2C2C2C]">{item.title}</p>
          <p className="text-xs text-[#2C2C2C]/50">Order {item.orderName} · {new Date(item.placedAt).toLocaleDateString("en-IN")}</p>
        </div>
        <button onClick={() => navigate({ to: "/products/$handle", params: { handle: item.handle } })} className="text-xs text-[#1E5EFF] hover:underline">
          View product
        </button>
      </div>

      <div className="mt-4 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`}>
            <Star size={26} strokeWidth={1.5} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-amber-300"} />
          </button>
        ))}
        <span className="ml-2 text-sm text-[#2C2C2C]/60">{rating > 0 ? `${rating} / 5` : "Tap to rate"}</span>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Headline (optional)"
        maxLength={120}
        className="mt-4 w-full rounded-lg border border-[#D9D9D6] bg-white px-3 py-2 text-sm"
      />
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What did you notice? Sleep, energy, digestion, skin, cycles…"
        rows={4}
        maxLength={2000}
        className="mt-3 w-full rounded-lg border border-[#D9D9D6] bg-white px-3 py-2 text-sm"
      />

      <div className="mt-3 flex items-center gap-3">
        <label className="cursor-pointer rounded-lg border border-dashed border-[#D9D9D6] px-3 py-2 text-xs text-[#2C2C2C]/70 hover:bg-[#F7F7F5]">
          {uploading ? "Uploading…" : "+ Add photo"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={uploading || images.length >= 2}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadFile(f);
              e.target.value = "";
            }}
          />
        </label>
        {images.map((url, i) => (
          <div key={i} className="relative">
            <img src={url} className="h-14 w-14 rounded border border-[#D9D9D6] object-cover" alt="" />
            <button
              onClick={() => setImages((arr) => arr.filter((_, j) => j !== i))}
              className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 shadow"
              aria-label="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <span className="ml-auto text-xs text-[#2C2C2C]/50">{images.length}/2</span>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button
          disabled={busy}
          onClick={submit}
          className="rounded-full bg-[#1E5EFF] px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit review"}
        </button>
      </div>
    </div>
  );
}
